import axios from "axios";
import { PLATFORM_CONFIG } from "../config/ApiConfig";
import { nutritionBody, nutritionHeaders, platformRequest } from "./PlatformAuth";

/** Nutrition service (new platform) calls used by the admin panel. */

const call = (method, path, data) =>
  platformRequest(PLATFORM_CONFIG.NUTRITION_API, method, `nutrition/${path}`, data);

/** { product, profile, health, ingredientFacts }; product/profile null when missing. */
export const getAdminNutrition = (productId) => call("get", `admin/products/${productId}`);

export const saveNutrition = (productId, body) => call("put", `products/${productId}`, body);

/** Health report for an unsaved label (live preview). */
export const previewScore = (body) => call("post", "score/preview", body);

export const getScoreHistory = (productId) => call("get", `products/${productId}/score-history`);

/**
 * Linked database records, the value per field from each and the conflicts:
 * { sources, fields: [{ field, name, unit, value, from, bySource, conflict }], provenance, conflicts }.
 */
export const getProductSources = (productId) => call("get", `admin/products/${productId}/sources`);

/** Catalog-wide counts: status, confidence, grades, completeness, categories, sources, history. */
export const getIntelligenceSummary = () => call("get", "admin/intelligence/summary");

/** Record every score under the current algorithm version; returns what moved. */
export const rescoreCatalog = () => call("post", "admin/intelligence/rescore", {});

/** Every scoring rule with its reference (read-only). */
export const getScoringRules = () => call("get", "admin/rules");

/** Stop using a record for the product; returns the updated comparison. */
export const unlinkSource = (productId, recordId) => call("delete", `admin/products/${productId}/sources/${recordId}`);

/**
 * Products whose nutrition needs work, most widely stocked first.
 * params: { status?, form?, micronutrients?, categoryId?, q?, page?, limit? }
 */
export const getWorklist = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  ).toString();
  return call("get", `admin/worklist${query ? `?${query}` : ""}`);
};

/**
 * Nutrition-expert review queue.
 * params: { review?: PENDING|APPROVED|REJECTED|ALL, source?: USDA|FATSECRET|FSSAI|EATPROTEIN, categoryId?, q?, page?, limit? }
 */
export const getReviews = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  ).toString();
  return call("get", `admin/reviews${query ? `?${query}` : ""}`);
};

/** Every approve / reject of a product, latest first. */
export const getReviewHistory = (productId) => call("get", `admin/reviews/${productId}`);

export const approveNutrition = (productId, note) =>
  call("post", `admin/reviews/${productId}/approve`, { note: note || undefined });

/** note (the reason) is required. */
export const rejectNutrition = (productId, note) => call("post", `admin/reviews/${productId}/reject`, { note });

/**
 * Best matches in Open Food Facts / USDA / FatSecret for { barcode?, name? }:
 * { best, candidates: [{ recordId, source, name, confidence, values, missing, url }], warnings }.
 * Stores the records on the server; changes no product.
 */
export const lookupNutrition = (query) => call("post", "admin/lookup", query);

/**
 * Nutrition of a mixed product added up from its ingredients and shares
 * ({ ingredients: "Rice 60%, Moong dal 30%, Ghee 5%, Salt", name?, categoryId?, subCategoryId? }):
 * { estimate: { values, calculated, assumedZero, unknown } | null, parts, warnings }. Saves nothing.
 */
export const estimateFromIngredients = (body) => call("post", "admin/estimate-from-ingredients", body);

/**
 * Fill and save a product's nutrition from external databases.
 * body: { barcode?, product?: { name, categoryId, subCategoryId?, description? }, sourceRecordId?, force? }
 */
export const autoFillProduct = (productId, body = {}) => call("post", `admin/products/${productId}/auto-fill`, body);

/** Catalog auto-fill job: progress, start ({ limit? }), cancel. */
export const getAutoFillBatch = () => call("get", "admin/auto-fill/batch");
export const startAutoFillBatch = (body = {}) => call("post", "admin/auto-fill/batch", body);
export const cancelAutoFillBatch = () => call("post", "admin/auto-fill/batch/cancel", {});

/**
 * What customers see (no sign-in needed): { health, ... } or null when the
 * product has no nutrition data on the new platform yet.
 */
export const getPublicNutrition = (productId) =>
  axios
    .get(`${PLATFORM_CONFIG.NUTRITION_API}nutrition/products/${productId}`, { headers: nutritionHeaders() })
    .then(({ data }) => nutritionBody(data))
    .catch((error) => {
      if (error.response?.status === 404) return null;
      throw new Error(error.response ? "Could not load nutrition" : "Nutrition service is not reachable");
    });

// ---- combos / weekly packs ----

/**
 * Products to add to a combo, by name or id:
 * { items: [{ id, name, hasNutrition, verified, servingGrams, per100g, isCombo, packs: [{ priceId, label, grams }] }] }.
 */
export const searchComboProducts = (search, ready = false) =>
  call("get", `admin/combo-products?${new URLSearchParams({ search, ...(ready && { ready: "true" }) }).toString()}`);

/**
 * A product's combo contents and the nutrition summed from them:
 * { combo: { days, people, problem } | null, fromName, items, problems, nutrition, saved }.
 */
export const getCombo = (productId) => call("get", `admin/combos/${productId}`);

/** Nutrition of unsaved items { productId, priceId?, grams?, quantity }[] (live preview). */
export const previewCombo = (items) => call("post", "admin/combos/preview", { items });

/** Replace the contents ({ items, days?, people? }); the product's nutrition is recalculated. */
export const saveCombo = (productId, body) => call("put", `admin/combos/${productId}`, body);

/** Stop calculating from contents; the last values stay and can be edited by hand. */
export const removeCombo = (productId) => call("delete", `admin/combos/${productId}`);
